import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { useSelector } from "react-redux";
import { Typography } from "@mui/material";

import messages from "../../../../../../assets/locale/messages";
import { UIButton } from "../../../../../../components/ui";
import SubscriptionPlanCard from "../../../../../../components/Company/Subscription/SubscriptionPlanCard";
import ChangeSubscriptionPlanModal from "../../../../../../components/Company/Subscription/ChangeSubscriptionPlanModal";
import { getUpgradeSubscriptionPlans } from "../../../../../../network/apis/subscriptionPlans";
import { changeCompanySubscriptionPlan } from "../../../../../../network/apis/companies";
import { getPortalApiErrorMessage } from "../../../../../../utils/companyPortal/eventApi";
import { showErrorMsg, showSuccessMsg } from "../../../../../../utils/Helpers";

import "../../../../../../components/Company/Subscription/Subscription.scss";

/**
 * Super Admin: the company's subscription plan and "Change Plan" (PROLINE-4122). The plan, its
 * dates, whether a higher plan exists and the higher plans themselves are the backend's (PROLINE-4150).
 */
const CompanySubscriptionTab = ({ company, onCompanyUpdated }) => {
  const lang = useSelector((state) => state.locale.lang);
  const { companiesList, shared } = messages[lang];
  const t = companiesList.subscription;
  const [plans, setPlans] = useState([]);
  const [changeOpen, setChangeOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  const plan = company?.subscription_plan;
  const isHighestPlan = Boolean(company?.on_highest_subscription_plan);

  // The plans above the current one, asked again once the plan changes.
  useEffect(() => {
    if (!plan?.id || isHighestPlan) {
      setPlans([]);
      return undefined;
    }
    let cancelled = false;
    // The error is toasted here once, not also by the interceptor.
    getUpgradeSubscriptionPlans(plan.id, { handlerEnabled: false })
      .then((response) => {
        if (!cancelled) setPlans(response?.data?.data ?? []);
      })
      .catch((error) => {
        if (!cancelled) showErrorMsg(getPortalApiErrorMessage(error, shared.generalErrMsg));
      });
    return () => {
      cancelled = true;
    };
  }, [plan?.id, isHighestPlan, shared.generalErrMsg]);

  if (!plan) return null;

  // The new plan applies at once; the billing year and its renewal date stay.
  const handleConfirm = async (nextPlan) => {
    setSaving(true);
    try {
      const response = await changeCompanySubscriptionPlan(company.id, nextPlan.id, {
        handlerEnabled: false,
      });
      onCompanyUpdated(response?.data?.data);
      setChangeOpen(false);
      // The backend's message, or ours while it sends none.
      const message = response?.data?.message;
      showSuccessMsg(message || t.changed, true);
    } catch (error) {
      // The company stays on its plan.
      showErrorMsg(getPortalApiErrorMessage(error, t.changeFailed));
    } finally {
      setSaving(false);
    }
  };

  const subscription = {
    subscription_plan: plan,
    start_date: company.subscription_plan_assigned_at,
    renewal_date: company.subscription_renewal_date,
  };

  return (
    <div className="company-subscription">
      <SubscriptionPlanCard subscription={subscription} />
      <div className="company-subscription__actions">
        {isHighestPlan && (
          <Typography component="p" className="text-body-sm company-subscription__highest-plan">
            {t.highestPlan}
          </Typography>
        )}
        <UIButton
          type="button"
          variant="outlined"
          color="primary"
          appearance="outline"
          disabled={isHighestPlan || !plans.length}
          onClick={() => setChangeOpen(true)}
        >
          {t.changePlan}
        </UIButton>
      </div>

      {changeOpen && (
        <ChangeSubscriptionPlanModal
          subscription={subscription}
          plans={plans}
          saving={saving}
          onClose={() => setChangeOpen(false)}
          onConfirm={handleConfirm}
        />
      )}
    </div>
  );
};

CompanySubscriptionTab.propTypes = {
  /**
   * The company details: `subscription_plan` (with `price_per_year` and its rates),
   * `subscription_plan_assigned_at`, `subscription_renewal_date` and `on_highest_subscription_plan`.
   */
  company: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    subscription_plan: PropTypes.object,
    subscription_plan_assigned_at: PropTypes.string,
    subscription_renewal_date: PropTypes.string,
    on_highest_subscription_plan: PropTypes.bool,
  }),
  /** Called with the company as the plan change returns it. */
  onCompanyUpdated: PropTypes.func,
};

CompanySubscriptionTab.defaultProps = {
  company: null,
  onCompanyUpdated: () => {},
};

export default CompanySubscriptionTab;
